import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TreeNode } from 'primeng/api';
import { BmPanelDirective } from './bm-panel.directive';
import { BaseService } from '../base.service';

export type navigationAction = 'prev' | 'next';

@Component({
  selector: 'app-base-menu-panel',
  templateUrl: './base-menu-panel.component.html',
  styleUrls: ['./base-menu-panel.component.scss'],
})
export class BaseMenuPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  loadedComponent: ComponentRef<any>;

  pCol3Height = 0;
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private cdr: ChangeDetectorRef,
    private baseService: BaseService
  ) {}

  @Input() items: TreeNode[] = [];
  @Input() expandOnlyUpLine = true;

  @Input() showNavButtons = false;

  @Input() set triggerNavigation(action: navigationAction) {
    this.triggerNavigate(action);
  }
  @Input() selectionMode: 'multiple' | 'single' = 'single';
  linearItems: any[] = [];
  selectedItem: any;
  totalItems = 0;
  totalChildren = 0;
  @ViewChild(BmPanelDirective) bmDirective: BmPanelDirective | undefined;
  index = 0;
  subNavEnded = false;
  nodeTobeExpanded: any[] = [];

  nextButtonClicked$: any;
  previousButtonClicked$: any;
  isPanelOpen = true;

  ngOnInit() {
    this.flattenTree(this.items);
    this.totalItems = this.linearItems.length;
  }

  flattenTree(items: any[]) {
    for (const item of items) {
      if (item.children) {
        this.nodeTobeExpanded.push(item);
      }
      if (this.nodeTobeExpanded.length) {
        item.nodesExpanded = [...this.nodeTobeExpanded];
      }

      this.linearItems.push(item);
      if (item.children) {
        this.flattenTree(item.children);
        this.nodeTobeExpanded.pop();
      }
    }
  }

  ngAfterViewInit(): void {
    const totalItemsCount = this.getTotalItemsLength();
    this.nextButtonClicked$ = this.getNextButtonClicked$(totalItemsCount);
    this.previousButtonClicked$ =
      this.getPreviousButtonClicked$(totalItemsCount);

    this.baseService.subNavigationEnded$.next(false);

    this.loadProcess(0);

    this.calculatePCol3Height();
  }

  private getPreviousButtonClicked$(totalItemsCount: number) {
    return this.baseService.previousButtonClicked$.subscribe(() => {
      if (this.index === totalItemsCount - 1) {
        this.subNavEnded = false;
      }
      if (this.index === 0) {
        this.subNavEnded = true;
      }

      if (this.index < totalItemsCount && !this.subNavEnded) {
        this.triggerNavigate('prev');
      } else {
        this.baseService.subNavigationEnded$.next(true);
      }
    });
  }

  private getNextButtonClicked$(totalItemsCount: number) {
    return this.baseService.nextButtonClicked$.subscribe(() => {
      if (this.index === totalItemsCount - 1) {
        this.subNavEnded = true;
      }
      if (this.index < totalItemsCount && !this.subNavEnded) {
        this.triggerNavigate('next');
      } else {
        this.baseService.subNavigationEnded$.next(true);
      }
    });
  }

  private getTotalItemsLength() {
    let totalItemsCount = this.items.length;

    totalItemsCount = this.mapChildren(totalItemsCount, this.items);

    return totalItemsCount;
  }

  private mapChildren(totalItemsCount: number, items: TreeNode[]) {
    items.map((item) => {
      if (item.children) {
        this.totalChildren += item.children.length;
        this.mapChildren(totalItemsCount, item.children);
      }
    });

    return totalItemsCount + this.totalChildren;
  }

  nodeSelect(evt: any) {
    const idx = this.linearItems.findIndex((node) => node === evt.node);
    this.loadProcess(idx);
  }

  expandNodes(index: number) {
    const oldExp = this.selectedItem?.nodesExpanded;
    const newExp = this.linearItems[index].nodesExpanded;

    const collapse =
      (newExp && oldExp?.filter((ex: any) => !newExp.includes(ex))) ||
      oldExp ||
      [];
    const expand =
      (oldExp && newExp?.filter((ex: any) => !oldExp.includes(ex))) ||
      newExp ||
      [];

    collapse.forEach((ex: any) => (ex.expanded = false));
    expand.forEach((ex: any) => (ex.expanded = true));
  }

  loadProcess(index: number, next = true) {
    let loadComponent = true;

    const instance = this.loadedComponent?.instance;
    if (instance && instance.onLeave) {
      loadComponent = instance.onLeave();
    }

    if (loadComponent) {
      if (index < 0 || index >= this.totalItems) {
        return;
      }

      this.index = index;
      const selectedItem = this.linearItems[index];
      // Expand and collapse Nodes
      if (this.expandOnlyUpLine) {
        this.expandNodes(index);
      } else if (selectedItem.children?.length > 0) {
        selectedItem.expanded = true;
      }
      this.selectedItem = selectedItem;

      const { component, data } = selectedItem;
      if (component) {
        const componentFactory =
          this.componentFactoryResolver.resolveComponentFactory(component);
        if (!this.bmDirective) {
          return;
        }
        const viewContainerRef = this.bmDirective.viewContainerRef;
        viewContainerRef.clear();

        this.loadedComponent =
          viewContainerRef.createComponent(componentFactory);

        this.loadedComponent.instance.data = data;

        this.cdr.detectChanges();
      } else {
        this.handleNavigation_WhenComponentUndefined(next, index);
      }
    }
  }

  private handleNavigation_WhenComponentUndefined(
    next: boolean,
    index: number
  ) {
    if (next) {
      this.loadProcess(++index);
    } else {
      if (index === 0) {
        this.baseService.triggerManualPrevNavigation$.next(true);
      } else {
        this.loadProcess(--index, false);
      }
    }
    return index;
  }

  triggerNavigate(action: navigationAction) {
    this.loadProcess(
      this.index + (action === 'next' ? 1 : -1),
      action === 'next'
    );
  }

  ngOnDestroy() {
    if (this.nextButtonClicked$) {
      this.nextButtonClicked$.unsubscribe();
    }
    if (this.previousButtonClicked$) {
      this.previousButtonClicked$.unsubscribe();
    }
    this.index = 0;
    this.subNavEnded = false;
    if (this.loadedComponent.instance.onLeave) {
      this.loadedComponent.instance.onLeave();
    }
  }

  private calculatePCol3Height() {
    this.pCol3Height = (
      document.getElementsByClassName('p-col-9')[0] as HTMLElement
    ).offsetHeight;
  }
}
